import { AsyncOperationState, Attribute, ObjectValues, Unionize } from "fond/types";
import { AddressCollection, ParcelCollection, PoleCollection, SpanCollection, StreetCollection } from "fond/types/geojson";

import { Icon } from "./icon";
import { BasicGeometryType, FilterConfiguration } from "./ProjectLayerConfig";
import { PresignedPostFields } from "./request";

/*
 * A Layer resource response as formed by the Service API.
 */
export type Layer = {
  ID: string;
  LayerKey: string;
  Attributes: Attribute[];
  Version: string;
  Geometry: null | "Null" | "Point" | "MultiPoint" | "LineString" | "MultiLineString" | "Polygon" | "MultiPolygon";
  Configuration: LayerConfiguration;
  Type: "Layer" | "CommentLayer";
  Url?: undefined | string;
  Fields?: undefined | PresignedPostFields;
};

export type LayerClassification = {
  Label: string;
  FilterConfiguration: Partial<FilterConfiguration>;
  GeometryType: Layer["Geometry"];
};

export type LayerConfiguration = {
  ID: string;
  LayerKey: string;
  Geometry: null | BasicGeometryType;
  Group: string;
  IsVisible: boolean;
  Hidden: boolean;
  Label: string;
};

export enum ImportStatus {
  STARTING = "STARTING",
  PENDING_UPLOAD = "PENDING_UPLOAD",
  CONVERTING = "CONVERTING",
  IMPORTING = "IMPORTING",
  BUILDING_TILES = "BUILDING_TILES",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export type LayerImportStatus = {
  ID: string;
  Version: { ID: string };
  LayerKey: string;
  ImportStatus: {
    Status: ImportStatus;
    StartedBy: string;
    StartedAt: string;
    EndedAt: null | string;
    Message: null | string;
  };
};

export type _Layer = {
  children: _Layer[];
  filter?: string[];
  icon?: Icon;
  id: string;
  isVisible: boolean;
  nameSingular: string;
};

export type LayerGroup = {
  id: string;
  groupName: string;
  layers: _Layer[];
};

export type LayerShape = {
  id: string;
  name: string;
  geometryType: string;
};

export type LayerGroupShape = {
  id: string;
  name: string;
  layers: LayerShape[];
};

export interface BaseDataLayers {
  "input_layers/demand": AddressCollection;
  "input_layers/parcel": ParcelCollection;
  "input_layers/ug_path": StreetCollection;
  "input_layers/aer_path": SpanCollection;
  "input_layers/aer_points": PoleCollection;
}

export type BaseDataLayerName = keyof BaseDataLayers;

export type LayerPropertySchema = {
  name: string;
  type?: "INTEGER" | "INTEGER64" | "REAL";
};

export type LayerPropertiesSchema = {
  [layerId: string]: LayerPropertySchema[];
};

export type LayerFeatureTotals = {
  [key: string]: {
    count: number | null;
    length: number | null;
    loadSubLayerStatus?: AsyncOperationState;
  };
};

export type LayerStatus = { layer: Layer | undefined; status: ImportStatus | undefined };

export type DesignLayers = Map<string, LayerStatus>;

export const LayerId = [
  "input_layers/demand",
  "input_layers/ug_path",
  "input_layers/ug_points",
  "input_layers/aer_path",
  "input_layers/aer_points",
  "input_layers/parcel",
  "input_layers/central_office",
  "input_layers/input_data_area",
  "input_layers/streets",
  "input_layers/leadins",
  "output/hub",
  "output/service_location",
  "output/splice",
  "output/fibre_cable",
  "output/hub/t1",
  "output/service_area",
] as const;
export type LayerId = Unionize<typeof LayerId>;

export const LayerIds: { [key: string]: LayerId } = {
  inAddress: "input_layers/demand",
  inParcel: "input_layers/parcel",
  inStreet: "input_layers/ug_path",
  inStreetPoints: "input_layers/ug_points",
  inBaseDataStreet: "input_layers/streets",
  inSpan: "input_layers/aer_path",
  inPole: "input_layers/aer_points",
  inExchange: "input_layers/central_office",
  inputDataArea: "input_layers/input_data_area",
  inLeadIns: "input_layers/leadins",
  hub: "output/hub",
  splice: "output/splice",
  fibreCable: "output/fibre_cable",
  t1Hub: "output/hub/t1",
  serviceLocation: "output/service_location",
  serviceArea: "output/service_area",
} as const;

export enum DesignLayerIds {
  INPUT_LAYER_BOUNDARY = "input_layers/base_data/boundary",
  INPUT_LAYER_UG_PATH = "input_layers/design_generation/ug_path",
  INPUT_LAYER_CONNECTION_POINTS = "input_layers/design_generation/connection_points",
  OUTPUT_DROP_CABLE = "output/design_generation/drop_cable",
}

export const NBNCTSInputLayerIds = {
  activityCode: "input_layers/cabling/activity_code",
  aoi: "input_layers/cabling/aoi",
  blocker: "input_layers/cabling/blocker",
  boundary: "input_layers/cabling/boundary",
  cadastre: "input_layers/cabling/cadastre",
  cable: "input_layers/cabling/cable",
  cableAssignment: "input_layers/cabling/cable_assignment",
  duct: "input_layers/cabling/duct",
  ductRoute: "input_layers/cabling/duct_route",
  equipment: "input_layers/cabling/equipment",
  equipmentHfc: "input_layers/cabling/equipment_hfc",
  equipmentPort: "input_layers/cabling/equipment_port",
  equipmentPortFrequency: "input_layers/cabling/equipment_port_frequency",
  equipmentPower: "input_layers/cabling/equipment_power",
  metadata: "input_layers/cabling/metadata",
  planningBrief: "input_layers/cabling/planning_brief",
  range: "input_layers/cabling/range",
  route: "input_layers/cabling/route",
  site: "input_layers/cabling/site",
  sjr: "input_layers/cabling/sjr",
  sl: "input_layers/cabling/sl",
  slToCopperPair: "input_layers/cabling/sl_to_copperpair",
  slToNe: "input_layers/cabling/sl_to_ne",
  splitterToEnclosure: "input_layers/cabling/splitter_to_enclosure",
  structurePoint: "input_layers/cabling/structurepoint",
} as const;

export type NBNCTSInputLayerId = ObjectValues<typeof NBNCTSInputLayerIds>;

export const NBNIDDLiteInputLayerIds = {
  activityCode: "input_layers/base_data/activity_code",
  aoi: "input_layers/base_data/aoi",
  awr: "input_layers/base_data/awr",
  blocker: "input_layers/base_data/blocker",
  boundary: "input_layers/base_data/boundary",
  cadastre: "input_layers/base_data/cadastre",
  cable: "input_layers/base_data/cable",
  cableAssignment: "input_layers/base_data/cable_assignment",
  duct: "input_layers/base_data/duct",
  ductRoute: "input_layers/base_data/duct_route",
  equipment: "input_layers/base_data/equipment",
  equipmentHfc: "input_layers/base_data/equipment_hfc",
  equipmentPort: "input_layers/base_data/equipment_port",
  equipmentPortFrequency: "input_layers/base_data/equipment_port_frequency",
  equipmentPower: "input_layers/base_data/equipment_power",
  metadata: "input_layers/base_data/metadata",
  planningBrief: "input_layers/base_data/planning_brief",
  range: "input_layers/base_data/range",
  route: "input_layers/base_data/route",
  site: "input_layers/base_data/site",
  sjr: "input_layers/base_data/sjr",
  sl: "input_layers/base_data/sl",
  slToCopperPair: "input_layers/base_data/sl_to_copperpair",
  slToNe: "input_layers/base_data/sl_to_ne",
  splitterToEnclosure: "input_layers/base_data/splitter_to_enclosure",
  structurePoint: "input_layers/base_data/structurepoint",
} as const;

export type NBNIDDLiteInputLayerIds = ObjectValues<typeof NBNIDDLiteInputLayerIds>;

export const ISGLayerIds = {
  boundary: "input_data/boundary",
  address: "input_data/address",
  parcels: "input_data/parcels",
  streets: "input_data/streets",
  leadins: "output/data_prep/leadins",
  waterways: "input_data/waterways",
  railways: "input_data/railways",
  demand: "output/data_prep/demand",
  ugPoints: "output/data_prep/ug_points",
  ugPath: "output/data_prep/ug_path",
  blockers: "output/solve/design_controls/blockers",
  hubs: "output/solve/design_controls/hubs",
  solveHubs: "output/solve/hubs",
} as const;

export type ISGLayerId = ObjectValues<typeof ISGLayerIds>;

export const MuPowerInputLayerIds = {
  addresses: "raw_data/addresses",
  parcels: "raw_data/parcels",
  streets: "raw_data/streets",
  poles: "data_prep/aer_points",
  boundary: "raw_data/boundary",
} as const;

export type MuPowerInputLayerId = ObjectValues<typeof MuPowerInputLayerIds>;

export const MuPowerEditableLayerIds = {
  demand: "data_prep/demand",
  ugPath: "data_prep/ug_path",
  ugPoints: "data_prep/ug_points",
  leadins: "data_prep/leadins",
} as const;
export type MuPowerEditableLayerId = ObjectValues<typeof MuPowerEditableLayerIds>;

export const MuPowerSolverLayerIds = {
  sourceHubs: "power_design/input_layers/source_hubs",
  primaryFootprint: "power_design/input_layers/primary_footprint",
  secondaryFootprint: "power_design/input_layers/secondary_footprint",
  primaryBlockers: "power_design/input_layers/primary_blockers",
  secondaryBlockers: "power_design/input_layers/secondary_blockers",
  transformerHubs: "power_design/input_layers/final_controls/transformer_hubs",
} as const;
export type MuPowerSolverLayerId = ObjectValues<typeof MuPowerSolverLayerIds>;

export const MuPowerOutputLayerIds = {
  sources: "output/sources",
  boxes: "output/boxes",
  meters: "output/meters",
  primaryWires: "output/primary_wires",
  secondaryWires: "output/secondary_wires",
  transformers: "output/transformers",
} as const;
export type MuPowerOutputLayerId = ObjectValues<typeof MuPowerOutputLayerIds>;
